import { userField } from './userField';

/**
 * Localize hosted listing-field and user-field labels/options for edit forms
 * and profile display. Hosted Console assets are English-only; known keys map
 * to ar.json / en.json, with the Console string as fallback so English stays
 * exactly as Console defined it when a translation is missing.
 */

const FIELD_LABEL_MESSAGE_IDS = {
  mode: 'OtherFilters.mode',
  language: 'OtherFilters.languageOfSession',
  visibility: 'EditListingDetailsForm.visibilityLabel',
  onlineMeeting: 'EditListingDetailsForm.onlineMeetingLabel',
  venueName: 'OtherFilters.venuesInUbud',
  listingOnSchoolBehalf: 'StaffOnlyFields.disableBooking',
};

/**
 * Console option values that should resolve to a canonical Language.* key.
 * Does not rewrite stored hosted values — lookup only.
 */
const LANGUAGE_OPTION_ALIASES = {
  ballinese: 'balinese',
};

/**
 * Canonical Language.* suffix for a hosted language option value.
 * Lowercases Console values (e.g. `Japanese` → `japanese`) and applies aliases
 * (e.g. misspelled `ballinese` → `balinese`).
 *
 * @param {string} optionValue
 * @returns {string}
 */
export const getLanguageOptionKey = optionValue => {
  const lowered = String(optionValue ?? '')
    .trim()
    .toLowerCase();
  return LANGUAGE_OPTION_ALIASES[lowered] || lowered;
};

const hasCatalogMessage = (intl, id) => {
  const catalog = intl?.messages;
  return !!(catalog && Object.prototype.hasOwnProperty.call(catalog, id) && catalog[id]);
};

const translate = (intl, id, fallback) => {
  if (!intl || !id) {
    return fallback || '';
  }
  if (!hasCatalogMessage(intl, id)) {
    return fallback || '';
  }
  return intl.formatMessage({ id, defaultMessage: fallback || '' });
};

const fieldFallbackLabel = fieldConfig =>
  fieldConfig?.saveConfig?.label || fieldConfig?.showConfig?.label || fieldConfig?.label || '';

/**
 * @param {object} fieldConfig
 * @param {object} intl
 * @returns {string}
 */
export const getListingFieldLabel = (fieldConfig, intl) => {
  const fallback = fieldFallbackLabel(fieldConfig);
  const key = fieldConfig?.key;
  if (!intl || !key) {
    return fallback;
  }
  const specificId = FIELD_LABEL_MESSAGE_IDS[key];
  return translate(intl, specificId || `UserField.${key}.label`, fallback);
};

/**
 * @param {object} fieldConfig
 * @param {object} intl
 * @param {string} [genericFallback]
 * @returns {string}
 */
export const getListingFieldPlaceholder = (fieldConfig, intl, genericFallback = '') => {
  const hosted = fieldConfig?.saveConfig?.placeholderMessage || '';
  const fallback = hosted || genericFallback || '';
  const key = fieldConfig?.key;
  if (!intl || !key) {
    return fallback;
  }
  return translate(intl, `UserField.${key}.placeholder`, fallback);
};

/**
 * @param {object} fieldConfig
 * @param {object} intl
 * @returns {string|null}
 */
export const getListingFieldSupportingText = (fieldConfig, intl) => {
  const hosted = fieldConfig?.saveConfig?.supportingText || '';
  const key = fieldConfig?.key;
  if (!intl || !key) {
    return hosted || null;
  }
  const text = translate(intl, `UserField.${key}.supportingText`, hosted);
  return text && text.trim() ? text : null;
};

/**
 * @param {object} fieldConfig
 * @param {object} intl
 * @param {string} [defaultRequired]
 * @returns {string}
 */
export const getListingFieldRequiredMessage = (fieldConfig, intl, defaultRequired = '') => {
  const hosted = fieldConfig?.saveConfig?.requiredMessage || '';
  const fallback = hosted || defaultRequired || '';
  const key = fieldConfig?.key;
  if (!intl || !key) {
    return fallback;
  }
  return translate(intl, `UserField.${key}.required`, fallback);
};

/**
 * @param {string} fieldKey
 * @param {{ option: string, label: string }} option
 * @param {object} intl
 * @returns {string}
 */
export const getListingFieldOptionLabel = (fieldKey, option, intl) => {
  const fallback = option?.label || option?.option || '';
  if (!intl || !option?.option) {
    return fallback;
  }
  if (fieldKey === 'mode') {
    return translate(intl, `Mode.${option.option}`, fallback);
  }
  if (fieldKey === 'language' || fieldKey === userField.LANGUAGES) {
    const languageKey = getLanguageOptionKey(option.option);
    return translate(intl, languageKey ? `Language.${languageKey}` : '', fallback);
  }
  if (fieldKey === 'visibility') {
    return translate(intl, `Visibility.${option.option}`, fallback);
  }
  if (fieldKey === userField.CATEGORIES) {
    return translate(intl, `Category.${option.option}`, fallback);
  }
  return translate(intl, `UserField.${fieldKey}.option.${option.option}`, fallback);
};

const LISTING_TYPE_MESSAGE_IDS = {
  individualSession: 'EditListingDetailsForm.listingTypeValue.individualSession',
  groupSession: 'EditListingDetailsForm.listingTypeValue.groupSession',
  'instant-booking': 'EditListingDetailsForm.listingTypeValue.groupSession',
  'free-booking': 'EditListingDetailsForm.listingTypePicker.free.label',
  'paylater-booking': 'EditListingDetailsForm.listingTypePicker.payLater.label',
  'digital-products-paid': 'EditListingDetailsForm.listingTypePicker.digitalPaid.label',
  'digital-products-free': 'EditListingDetailsForm.listingTypePicker.digitalFree.label',
};

/**
 * Hosted listing-type labels are English-only. Map known types to ar/en keys.
 *
 * @param {string} listingType
 * @param {object} intl
 * @param {string} [fallback]
 * @returns {string}
 */
export const getListingTypeLabel = (listingType, intl, fallback) => {
  const hosted = fallback || listingType || '';
  if (!intl || !listingType) {
    return hosted;
  }
  return translate(intl, LISTING_TYPE_MESSAGE_IDS[listingType], hosted);
};
